import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { Subscription } from 'rxjs';
import { NotificationService } from 'src/app/services/api/common/notification.service';
import { SharedModule } from 'src/app/modules/shared/shared.module';
import { Router, RouterModule } from '@angular/router';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import * as moment from 'moment';
import { UserNotification, UserNotificationsPayload } from 'src/app/models/user-notification.model';

@Component({
  selector: 'app-notification-ring',
  templateUrl: './notification-ring.component.html',
  styleUrls: ['./notification-ring.component.scss'],
  standalone: true,
  imports: [CommonModule, NgScrollbarModule, SharedModule, RouterModule, NgbTooltipModule],
})
export class NotificationRingComponent implements OnInit, OnDestroy {
  private readonly severityMap: Record<string, { icon: string; accent: string; softAccent: string; }> = {
    critical: { icon: 'fa-solid fa-light-emergency-on', accent: '#7f1d1d', softAccent: 'rgba(127, 29, 29, 0.16)' },
    high: { icon: 'fa-solid fa-diamond-exclamation', accent: '#dc2626', softAccent: 'rgba(220, 38, 38, 0.16)' },
    medium: { icon: 'fa-solid fa-triangle-exclamation', accent: '#f59e0b', softAccent: 'rgba(245, 158, 11, 0.18)' },
    low: { icon: 'fa-solid fa-circle-exclamation', accent: '#6b7280', softAccent: 'rgba(107, 114, 128, 0.16)' }
  };
  notifications: UserNotification[] = [];
  notificationCount: number = 0;
  subscription: Subscription;
  markReadSubscription: Subscription;

  constructor(
    private notifService: NotificationService,
    private router: Router) {

    try {this.subscription.unsubscribe();} catch (error) {}
  }
  ngOnInit(): void {
    this.notifService.markedAsRead$.subscribe((val) => {
      this.getNewNotifications()
    })
    this.getNewNotifications();
  }

  getNewNotifications() {
    const payload: UserNotificationsPayload = {
      page: 0,
      page_size: 10,
      search_text: '',
      search_list: [],
      sort_column: 'timestamp',
      sort_type: 'DESC'
    };

    this.notifService.getUserNotifications(payload).subscribe({
      next: response => {
        const responseData = response?.data;
        const items = Array.isArray(responseData?.data) ? responseData.data : [];
        this.notifications = items.map(notification => ({
          ...notification,
          read: notification.read === true,
          subject: notification?.details?.subject ?? notification.notification_type,
          description: notification?.details?.description ?? '',
          contact_methods_label: notification?.contact_methods?.join(', ') ?? ''
        }));
        this.notifications.sort((a, b) => b.timestamp - a.timestamp);
        this.notificationCount = this.notifications.filter(notification => !notification.read).length;
      },
      error: err => {}
    })
  }

  detailNotification(notificationId: string, notificationGroupId?: string) {
    if (notificationGroupId) {
      this.markAsRead(notificationGroupId);
    }
    this.router.navigate(['app/alarms/notifications/list']);
  }

  detailNotificationGroup(notification: UserNotification) {
    this.markAsRead(notification.notification_group_id);
    this.router.navigate(['app/alarms/notifications/list']);
  }

  markAsRead(notificationGroupId: string) {
    this.notifService.notificationMarkGroupRead(notificationGroupId).subscribe({
      next: response => {
        this.getNewNotifications();
      },
      error: err => {}
    })
  }

  markAllAsRead() {
    this.notifService.notificationMarkAllGroupsRead().subscribe({
      next: response => {
        this.getNewNotifications();
        //this.notifService.createTestNotifications().subscribe({});
      },
      error: err => {}
    })
  }

  getTimeEclipse(timestamp: number): string {

    try {
      return moment(timestamp).fromNow();
    } catch (error) { return "";}
  }

  getNotificationIconClass(notification: UserNotification): string {
    const severityMeta = this.getNotificationSeverityMeta(notification);
    const iconName = String(notification?.icon || severityMeta?.icon || 'bell').trim();

    if (!iconName) {
      return 'fa-solid fa-bell';
    }

    if (iconName.includes('fa-') || iconName.includes('ti-')) {
      return iconName;
    }

    return `fa-solid fa-${iconName}`;
  }

  getNotificationColor(notification: UserNotification): string {
    return notification?.color || this.getNotificationSeverityMeta(notification)?.accent || '#dc2626';
  }

  getNotificationIconBackground(notification: UserNotification): string {
    const severityMeta = this.getNotificationSeverityMeta(notification);

    if (!notification?.color && severityMeta?.softAccent) {
      return severityMeta.softAccent;
    }

    const color = this.getNotificationColor(notification);
    const hexMatch = color.match(/^#([0-9a-f]{6})$/i);

    if (!hexMatch) {
      return 'rgba(220, 38, 38, 0.12)';
    }

    const hex = hexMatch[1];
    const red = parseInt(hex.slice(0, 2), 16);
    const green = parseInt(hex.slice(2, 4), 16);
    const blue = parseInt(hex.slice(4, 6), 16);

    return `rgba(${red}, ${green}, ${blue}, 0.12)`;
  }

  private getNotificationSeverityMeta(notification: UserNotification): { icon: string; accent: string; softAccent: string; } | null {
    const severity = String(notification?.severity || notification?.alarm_severity || '').trim().toLowerCase();
    return this.severityMap[severity] || null;
  }

  ngOnDestroy(): void {
    try {
      this.subscription.unsubscribe();
      // this.markReadSubscription.unsubscribe();
    } catch (error) {}
  }

}
